import { showErrorToast } from "@configs/react-toastify/snackbar";
import { uppercase, formatVerificationType } from "@utils/common";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ITEMS_PER_PAGE_KEY, DEFAULT_ITEMS_PER_PAGE } from "@constants/table";
import useItemsPerPage from "@hooks/useItemsPerPage";
import TableWithPagination from "../../../components/Tables/TableWithPagination";
import { HorizontalBarChart } from "../../../components/UI";
import Badge from "../../../components/UI/Badge";
import CustomSelect from "../../../components/UI/CustomSelect";
import { CustomDatePicker } from "../../../components/Forms";
import queryKeys from "../../../configs/react-query/query-key";
import { useMutationData } from "../../../hooks/useMutationData";
import { useSearchInfo } from "../../../hooks/useSearchInfo";
import { useTheme } from "@hooks/useTheme";
import { ComponentHeader } from "@components/Tables/ComponentHeader";
import LoadingSpinner from "@components/UI/LoadingSpinner";
import useDelayedLoadingOverlay from "@hooks/useDelayedLoadingOverlay";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { ChevronUpIcon } from "@components/Icons";

export default function ComponentUsageStatus() {
  const navigate = useNavigate();
  const { colors } = useTheme();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchFilters, setSearchFilters] = useState({});
  const [topComponentUsageList, setTopComponentUsageList] = useState([]);
  const [topComponentUsages, setTopComponentUsages] = useState([]);
  const [totalData, setTotalData] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const { itemsPerPage, handleItemsPerPageChange } = useItemsPerPage(ITEMS_PER_PAGE_KEY.COMPONENT_USAGE_STATUS, DEFAULT_ITEMS_PER_PAGE);
  const { itemsPerPage: componentInfoItemsPerPage, handleItemsPerPageChange: handleComponentInfoItemsPerPageChange } = useItemsPerPage(ITEMS_PER_PAGE_KEY.COMPONENT_INFO_TABLE, DEFAULT_ITEMS_PER_PAGE);
  const [selectedRows, setSelectedRows] = useState([]);
  const [dateFilter, setDateFilter] = useState(null);
  const [customDateRange, setCustomDateRange] = useState({ startDate: null, endDate: null });
  const [isExpandedTop, setIsExpandedTop] = useState(true);
  const [isExpandedInfo, setIsExpandedInfo] = useState(true);

  // Component information table states
  const [componentInfoData, setComponentInfoData] = useState([]);
  const [componentInfoCurrentPage, setComponentInfoCurrentPage] = useState(1);
  const [componentInfoTotalPages, setComponentInfoTotalPages] = useState(0);
  const [componentInfoTotalData, setComponentInfoTotalData] = useState(0);
  const [selectedComponent, setSelectedComponent] = useState(null);
  const hasInitialFetchRef = useRef(false);
  const componentInfoTableRef = useRef(null);
  const isFetchingRef = useRef(false);
  const isComponentInfoFetchingRef = useRef(false);
  const [isSearching, setIsSearching] = useState(false);
  const debounceTimerRef = useRef(null);

  const { productsList, fetchSearchInfo } = useSearchInfo();
  const { t } = useTranslation();

  // Initial data fetch - only run once on mount
  useEffect(() => {
    if (!hasInitialFetchRef.current && !isFetchingRef.current) {
      hasInitialFetchRef.current = true;
      fetchSearchInfo();
      fetchData();
    }
  }, []);

  // Fetch data when currentPage changes (skip initial load)
  useEffect(() => {
    if (!hasInitialFetchRef.current || isFetchingRef.current) return;
    fetchData();
  }, [currentPage]);

  // Handle search filters changes (skip initial load)
  useEffect(() => {
    if (!hasInitialFetchRef.current || isFetchingRef.current) return;
    
    if (currentPage !== 1) {
      setCurrentPage(1);
    } else {
      fetchData();
    }
  }, [searchFilters]);

  // Call API when itemsPerPage changes
  useEffect(() => {
    if (!hasInitialFetchRef.current || isFetchingRef.current) return;
    fetchData();
  }, [itemsPerPage]);

  // Handle date filter changes (skip initial load)
  useEffect(() => {
    if (!hasInitialFetchRef.current || isFetchingRef.current) return;

    // Skip if this is triggered by custom date range change
    if (dateFilter === "custom") return;

    if (currentPage !== 1) {
      setCurrentPage(1);
    } else {
      fetchData();
    }
  }, [dateFilter]);

  // Handle custom date range changes
  useEffect(() => {
    if (customDateRange.startDate && customDateRange.endDate) {
      setDateFilter("custom");
    }
  }, [customDateRange.startDate, customDateRange.endDate]);

  // Fetch component information when selectedComponent or product filter or product component or componentInfoItemsPerPagechanges changes
  useEffect(() => {
    if (!hasInitialFetchRef.current) return;

    const componentName = selectedComponent || searchFilters.component;
    if (componentName) {
      fetchComponentInformation(componentName);
    } else {
      // Clear component information when no component is selected
      setComponentInfoData([]);
      setComponentInfoTotalData(0);
      setComponentInfoTotalPages(0);
      setComponentInfoCurrentPage(1);
    }
  }, [selectedComponent, componentInfoCurrentPage, searchFilters.productSeq, searchFilters.component, componentInfoItemsPerPage]);

  const columns = ["componentName", "componentCount"];

  // Component information table columns
  const componentInfoColumns = [
    "component",
    "projectId",
    "projectName",
    "verificationType",
    "verificationVersion",
    "productName",
  ];

  const columnConfig = {
    componentName: {
      headerRender: () => t("open-source.component-name"),
      customRender: (value) => {
        const text = value != null && String(value).trim() !== "" ? String(value) : "-";
        return (
          <div className="min-w-0 w-full truncate text-left" title={text !== "-" ? text : undefined}>
            {text}
          </div>
        );
      },
      style: {
        width: "75%",
      },
    },
    componentCount: {
      headerRender: () => t("status.component-count"),
      style: {
        width: "25%",
      },
    },
  };

  // Component information table column configuration
  const componentInfoColumnConfig = {
    component: {
      headerRender: () => <ComponentHeader/>,
      customRender: (value, row) => (
        <div>
          <div className="text-sm">
            {row.component || "-"} ({row.componentVersion})
          </div>
          <div className={`text-xs ${colors.textNeutralMuted}`}>{row.componentLicense || "-"}</div>
        </div>
      ),
      style: {
        width: "25%",
      },
    },
    projectId: {
      headerRender: () => t("status.project-id"),
    },
    projectName: {
      headerRender: () => t("status.project-name"),
    },
    verificationType: {
      headerRender: () => t("status.verification-type"),
      customRender: (value) => formatVerificationType(value),
    },
    verificationVersion: {
      headerRender: () => t("status.verification-version"),
    },
    productName: {
      headerRender: () => t("project.clarity.product-name"),
      customRender: (_value, row) => {
        const name = row?.product?.productName || row?.productName || "---";
        const text = name != null && String(name).trim() !== "" ? String(name) : "---";
        return (
          <div className="min-w-0 w-full truncate text-left" title={text !== "---" ? text : undefined}>
            {text}
          </div>
        );
      },
    },
  };


  const handleCheckboxChange = (newSelectedRows) => {
    setSelectedRows([...newSelectedRows]);
  };

  const handleItemsPerPageChangeWithReset = (newItemsPerPage) => {
    handleItemsPerPageChange(newItemsPerPage);
    setCurrentPage(1); // Reset to page 1 when changing number of rows
  };

  const handleComponentInfoItemsPerPageChangeWithReset = (newItemsPerPage) => {
    handleComponentInfoItemsPerPageChange(newItemsPerPage);
    setComponentInfoCurrentPage(1); // Reset to page 1 when changing number of rows
  };

  const onRowClick = (rowData) => {
    // Set selected component and reset page to 1 when clicking on row
    setSelectedComponent(rowData.componentName);
    setComponentInfoCurrentPage(1);
    setIsExpandedInfo(true);

    // Scroll to component information table after a short delay to ensure it's rendered
    setTimeout(() => {
      if (componentInfoTableRef.current) {
        componentInfoTableRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    }, 100);
  };

  // Handle click on component information table row
  const onComponentInfoRowClick = (rowData) => {
    const projectId = rowData.projectSeq || rowData.projectId;
    const verificationId = rowData.verificationSeq || rowData.verificationId;
    const verificationType = rowData.verificationType;
    if (!projectId || !verificationId || !verificationType) {
      return;
    }

    const routes = {
      clarity: `../projects/${projectId}/clarity/${verificationId}`,
      fossid: `../projects/${projectId}/fossid/${verificationId}`,
      sbom: `../projects/${projectId}/sbom/${verificationId}`,
      clarityair: `../projects/${projectId}/clarity-air/${verificationId}`,
    };

    if (routes[verificationType]) {
      const route = routes[verificationType];
      navigate(route);
    } else {
    }
  };

  const getListData = useMutationData(
    queryKeys.getComponentUsageData,
    { method: "POST" },
    {
      onSuccess: (response) => {
        if (typeof response !== "string") {
          setTopComponentUsageList(response.object || []);
          const totalCount = response.objectCount || 0;
          setTotalData(totalCount);
          setTotalPages(Math.ceil(totalCount / itemsPerPage));
          
          if (currentPage === 1) {
            setTopComponentUsages(response.object || []);
            // Only auto-select first component on initial load, not on filter changes
            const hasSearchKeyword = searchFilters.component && searchFilters.component.trim() !== "";
            const shouldAutoSelect = !selectedComponent && !hasSearchKeyword;
            if (shouldAutoSelect && response.object && response.object.length > 0) {
              setSelectedComponent(response.object[0].componentName);
            }
          }
        }
      },
      onError: (error) => {
        showErrorToast(t("preferences.loading-data-error"));
        console.error("Error fetching component usage data:", error);
      },
    }
  );


  const fetchData = useCallback(async () => {
    // Prevent multiple simultaneous calls
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;

    const bodyRequest = Object.keys(searchFilters).reduce((acc, key) => {
      const value = searchFilters[key];
      if (value !== "" && value !== undefined) {
        acc[key] = value;
      }
      return acc;
    }, {});

    // Add date range filter if dateFilter is set
    if (dateFilter) {
      // Helper function to get date range based on filter
      const getDateRange = (filter) => {
        const today = new Date();
        const todayStr = today.toLocaleDateString("en-CA"); // YYYY-MM-DD format

        switch (filter) {
          case "7days":
            const sevenDaysAgo = new Date(today);
            sevenDaysAgo.setDate(today.getDate() - 7);
            const sevenDaysAgoStr = sevenDaysAgo.toLocaleDateString("en-CA"); // YYYY-MM-DD format
            return {
              updatedAtFrom: sevenDaysAgoStr,
              updatedAtTo: todayStr,
            };
          case "month":
            const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
            const firstDayStr = firstDayOfMonth.toLocaleDateString("en-CA"); // YYYY-MM-DD format
            return {
              updatedAtFrom: firstDayStr,
              updatedAtTo: todayStr,
            };
          case "custom":
            // For custom date range, use the current customDateRange state
            if (customDateRange.startDate && customDateRange.endDate) {
              const startDateStr = customDateRange.startDate.toLocaleDateString("en-CA"); // YYYY-MM-DD format
              const endDateStr = customDateRange.endDate.toLocaleDateString("en-CA"); // YYYY-MM-DD format
              return {
                updatedAtFrom: startDateStr,
                updatedAtTo: endDateStr,
              };
            }
            return {};
          default:
            return {};
        }
      };

      const dateRange = getDateRange(dateFilter);

      if (dateRange.updatedAtTo) {
        // Create date in local timezone to avoid timezone issues
        const [year, month, day] = dateRange.updatedAtTo.split("-").map(Number);
        const updatedAtToDate = new Date(year, month - 1, day); // month is 0-indexed
        dateRange.updatedAtTo = updatedAtToDate.toLocaleDateString("en-CA"); // YYYY-MM-DD format
      }

      Object.assign(bodyRequest, dateRange);
    }

    bodyRequest["pageSize"] = itemsPerPage;
    bodyRequest["startRow"] = itemsPerPage * (currentPage - 1);

    getListData.mutate(bodyRequest, {
      onSettled: () => {
        isFetchingRef.current = false;
      },
    });
  }, [searchFilters, dateFilter, itemsPerPage, currentPage, customDateRange, getListData]);

  const fetchDataWithCustomDates = useCallback(
    async (startDateStr, endDateStr) => {
      // Prevent multiple simultaneous calls
      if (isFetchingRef.current) return;
      isFetchingRef.current = true;

      // Create date in local timezone to avoid timezone issues
      const [year, month, day] = endDateStr.split("-").map(Number);
      const endDate = new Date(year, month - 1, day); // month is 0-indexed
      const updatedEndDateStr = endDate.toLocaleDateString("en-CA"); // YYYY-MM-DD format

      const bodyRequest = {
        ...searchFilters, // Keep existing search filters
        updatedAtFrom: startDateStr,
        updatedAtTo: updatedEndDateStr,
        pageSize: itemsPerPage,
        startRow: itemsPerPage * (currentPage - 1),
      };

      getListData.mutate(bodyRequest, {
        onSettled: () => {
          isFetchingRef.current = false;
        },
      });
    },
    [searchFilters, itemsPerPage, currentPage, getListData],
  );

  // Component information data mutation
  const getComponentInfoData = useMutationData(
    queryKeys.getComponentUsageInformation,
    { method: "POST" },
    {
      onSuccess: (response) => {
        if (typeof response !== "string") {
          setComponentInfoData(response.object);
          setComponentInfoTotalData(response.objectCount);
          setComponentInfoTotalPages(Math.ceil(response.objectCount / componentInfoItemsPerPage));
        }
      },
      onError: (error) => {
        showErrorToast(t("preferences.loading-data-error"));
        console.error("Error during get component information:", error);
      },
    },
  );

  // Fetch component information data
  const fetchComponentInformation = async (componentNameParam) => {
    const componentName = componentNameParam || selectedComponent || searchFilters.component;
    if (!componentName || isComponentInfoFetchingRef.current) return;
    isComponentInfoFetchingRef.current = true;

    const bodyRequest = {
      pageSize: componentInfoItemsPerPage,
      startRow: componentInfoItemsPerPage * (componentInfoCurrentPage - 1),
      component: componentName,
      exactMatch: !!selectedComponent,
    };

    // Add productSeq to payload if product is selected in search filters
    if (searchFilters.productSeq && searchFilters.productSeq !== "") {
      bodyRequest.productSeq = searchFilters.productSeq;
    }

    getComponentInfoData.mutate(bodyRequest, {
      onSettled: () => {
        isComponentInfoFetchingRef.current = false;
      },
    });
  };

  const searchHandler = useCallback((inputData) => {
    setSearchFilters(prev => ({
      ...prev,
      ...inputData,
    }));
  }, []);

  const handleSearchChange = useCallback((value) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    setIsSearching(true);

    debounceTimerRef.current = setTimeout(() => {
      const trimmedValue = value.trim();

      if (trimmedValue === "") {
        setSearchFilters(prev => ({
          ...prev,
          component: ""
        }));
      } else {
        searchHandler({ component: trimmedValue });
        setSelectedComponent(null);
      }

      setComponentInfoCurrentPage(1);
      setIsSearching(false);
    }, 500);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchHandler]);

  const handleProductChange = useCallback((selectedOption) => {
    const productSeq = selectedOption ? selectedOption.value : "";
    setSearchFilters(prev => ({
      ...prev,
      productSeq: productSeq
    }));
  }, []);
  const showListLoadingOverlay = useDelayedLoadingOverlay(getListData.isPending);
  const showComponentInfoLoadingOverlay = useDelayedLoadingOverlay(getComponentInfoData.isPending);
  const shouldShowListSkeleton = getListData.isPending && topComponentUsageList.length === 0;
  const shouldShowListOverlay = showListLoadingOverlay && topComponentUsageList.length > 0;
  const shouldShowComponentInfoSkeleton = getComponentInfoData.isPending && componentInfoData.length === 0;
  const shouldShowComponentInfoOverlay = showComponentInfoLoadingOverlay && componentInfoData.length > 0;

  return (
    <div className={`flex flex-col ${colors.bgSecondary}`}>
      {/* Header */}
      <div className={`${colors.textPlaceholder} py-1`}>
        <div className="flex items-center justify-between">
          <h1 className={`text-[28px] uppercase font-bold ${colors.textPrimary}`}>
            {(t("status.component-usage-status-title"))}
          </h1>
        </div>
      </div>

      {/* Controls */}
      <div className={`flex-shrink-0 pt-4 pr-4 ${colors.bgSecondary}`}>
        <div className="mb-4 flex flex-row space-y-0 items-center justify-between">
          {/* Search section */}
          <div className="flex flex-row space-y-0 items-center space-x-4">
            <div className="relative">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <MagnifyingGlassIcon className={`h-5 w-5 ${colors.textMuted}`} />
              </div>
              <input
                type="text"
                placeholder={t("common.search-placeholder")}
                onChange={(e) => {
                  handleSearchChange(e.target.value);
                }}
                className={`block w-96 border py-2 pr-3 pl-10 h-10 ${colors.borderPrimary} rounded-md leading-5 ${colors.bgSecondary} ${colors.textPrimary} ${colors.borderHoverPrimary} ${colors.textActive} outline-none sm:text-sm`}
              />
              {isSearching && (
                <div className="absolute top-1/2 right-3 -translate-y-1/2 transform">
                  <div className={`h-3 w-3 animate-spin rounded-full border-b-2 ${colors.borderLoader}`}></div>
                </div>
              )}
            </div>
            
            {/* Product Select */}
            <div className="w-64">
              <CustomSelect
                options={productsList.map(product => ({
                  value: product.value,
                  label: product.label
                }))}
                value={searchFilters.productSeq ? {
                  value: searchFilters.productSeq,
                  label: productsList.find(p => p.value === searchFilters.productSeq)?.label || searchFilters.productSeq
                } : null}
                onChange={handleProductChange}
                placeholder={t("status.select-product")}
                size="md"
                className="w-full text-sm font-medium"
                clearable={true}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Section */}
      <div className={`rounded-2xl p-6 ${colors.bgPrimary} ${colors.borderPrimary} border`}>
        <div className={'grid grid-cols-1 gap-y-4 mb-3'}>
          <div
            className="flex cursor-pointer items-center justify-between"
            onClick={() => setIsExpandedTop(!isExpandedTop)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setIsExpandedTop(!isExpandedTop);
              }
            }}
          >
            <div className="flex items-center">
              <ChevronUpIcon
                className={`mr-2 h-5 w-5 transition-transform duration-200 ${colors.textSecondary} ${!isExpandedTop ? "rotate-180" : ""}`}
              />
              <h2 className={`text-lg font-semibold ${colors.textPrimary}`}>
                {uppercase(t("status.component-usage-chart-title"))}
              </h2>
            </div>
          </div>

          {isExpandedTop && (
            <>
              <div className="flex items-center gap-3 md:flex-wrap lg:flex-nowrap">
                <button
                  onClick={() => {
                    if (dateFilter === "7days") {
                      setDateFilter(null);
                    } else {
                      setDateFilter("7days");
                      setCustomDateRange({ startDate: null, endDate: null });
                    }
                  }}
                  className={`rounded-full px-4 py-1 text-sm font-medium transition-colors ${
                    dateFilter === "7days"
                      ? `${colors.bgBasicInfo} ${colors.textPrimary} border ${colors.borderPrimary}`
                      : `${colors.bgPrimary} ${colors.textPrimary} border ${colors.borderPrimary}`
                  }`}
                >
                  {t("status.last-7-days")}
                </button>
                <button
                  onClick={() => {
                    if (dateFilter === "month") {
                      setDateFilter(null);
                    } else {
                      setDateFilter("month");
                      setCustomDateRange({ startDate: null, endDate: null });
                    }
                  }}
                  className={`rounded-full px-4 py-1 text-sm font-medium transition-colors ${
                    dateFilter === "month"
                      ? `${colors.bgBasicInfo} ${colors.textPrimary} border ${colors.borderPrimary}`
                      : `${colors.bgPrimary} ${colors.textPrimary} border ${colors.borderPrimary}`
                  }`}
                >
                  {t("status.this-month")}
                </button>
                {/* Vertical separator line */}
                <div className={`h-4 border ${colors.borderPrimary}`} style={{ width: "0.0625rem" }}></div>
                {/* Custom date range input */}
                <div className="flex items-center space-x-2">
                  <span className={`text-sm ${colors.textSecondary}`}>{t("status.custom")}:</span>
                  <CustomDatePicker
                    rangeSeparator=' → '
                    selected={customDateRange.startDate}
                    onChange={(dates) => {
                      const [start, end] = dates;
                      setCustomDateRange({ startDate: start, endDate: end });
                      if (start && end) {
                        setDateFilter("custom");
                        setCurrentPage(1);
                        // Trigger search with custom date range directly
                        // Use local date formatting to avoid timezone issues
                        const startDateStr = start.toLocaleDateString("en-CA"); // YYYY-MM-DD format
                        const endDateStr = end.toLocaleDateString("en-CA"); // YYYY-MM-DD format
                        fetchDataWithCustomDates(startDateStr, endDateStr);
                      } else if (!start && !end) {
                        setDateFilter(null);
                      }
                    }}
                    startDate={customDateRange.startDate}
                    endDate={customDateRange.endDate}
                    selectsRange
                    placeholderText={t("status.select-date-range")}
                    className={`h-8 w-56 appearance-none rounded-lg border p-2 text-sm font-medium focus:outline-none ${colors.bgInput} ${colors.borderPrimary} ${colors.textPrimary} ${colors.textPlaceholder} ${colors.borderHoverPrimary}`}
                    dateFormat="yyyy-MM-dd"
                    size="sm"
                  />
                </div>
              </div>

              <div className={`grid grid-cols-1 xl:grid-cols-2 gap-6`}>
                {/* Left Column - Chart */}
                <div className={`p-6 rounded-2xl ${colors.bgBasicInfo}`}>
                  <div className="mb-4 relative pb-2">
                    <h2 className={`text-lg font-semibold ${colors.textPrimary}`}>{uppercase(t("navbar.dashboard"))}</h2>
                    <p className={`text-xs ${colors.textSecondary} pb-4`}>{t("status.top-10-highest-usage-components")}</p>
                    <div
                      className={`h-px border ${colors.borderPrimary}`}
                      style={{ marginLeft: "-24px", marginRight: "-24px" }}
                    ></div>
                  </div>

                  <HorizontalBarChart
                    data={topComponentUsages.map((item) => ({
                      name: item.componentName,
                      count: item.componentCount,
                    }))}
                    colors={colors}
                    maxItems={10}
                    usageLabel="status.component-usage"
                  />
                </div>
                {/* Right Column - Table with Filter */}
                <div className={`p-6 rounded-2xl ${colors.bgBasicInfo}`}>
                  <div className="mb-4 flex items-center justify-between">
                    <h2 className={`text-lg font-semibold ${colors.textPrimary}`}>
                      {uppercase(t("status.list-of-component-usage"))}
                    </h2>
                    <Badge
                      variant="danger"
                      size="md"
                      className={`rounded-full font-medium ${colors.textPrimary}`}
                      style={{ backgroundColor: "#CA2C26" }}
                    >
                      <p className={`font-medium ${colors.textPrimary}`}>
                        {t("status.total")}: {totalData}
                      </p>
                    </Badge>
                  </div>
                  <div className="relative">
                    <TableWithPagination
                      columnConfig={columnConfig}
                      data={topComponentUsageList}
                      columns={columns}
                      onRowClick={isComponentInfoFetchingRef.current ? null : onRowClick} // disable row click while api is fetching
                      loading={shouldShowListSkeleton}
                      totalPages={totalPages}
                      totalData={totalData}
                      currentPage={currentPage}
                      setCurrentPage={setCurrentPage}
                      maxRowsPerPage={itemsPerPage}
                      onItemsPerPageChange={handleItemsPerPageChangeWithReset}
                      selectedRows={selectedRows}
                      onCheckboxChange={handleCheckboxChange}
                      disabledRow={[]}
                      hasRadioButton={false}
                      shouldShowGoToPage={true}
                      tableStyle="default"
                      showItemsPerPageSelector={true}
                      scrollY="500px"
                    />
                    {shouldShowListOverlay && (
                      <div className="pointer-events-none absolute inset-0 z-10">
                        <LoadingSpinner bgClass="bg-transparent" size={8} data-testid="component-usage-list-loading-overlay" />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Component Information Table Section */}
      <div className="flex-1 pt-6" ref={componentInfoTableRef}>
        <div className={`rounded-2xl p-6 ${colors.bgPrimary} ${colors.borderPrimary} border`}>
          <div
            className="mb-4 flex cursor-pointer items-center justify-between"
            onClick={() => setIsExpandedInfo(!isExpandedInfo)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setIsExpandedInfo(!isExpandedInfo);
              }
            }}
          >
            <div className="flex items-center">
              <ChevronUpIcon
                className={`mr-2 h-5 w-5 transition-transform duration-200 ${colors.textSecondary} ${!isExpandedInfo ? "rotate-180" : ""}`}
              />
              <h2 className={`text-lg font-semibold ${colors.textPrimary}`}>
                {uppercase(t("status.component-usage-by-projects"))}
              </h2>
            </div>
          </div>

          {isExpandedInfo && (
            <>
              <div>
                <div className="relative">
                  <TableWithPagination
                    columnConfig={componentInfoColumnConfig}
                    data={componentInfoData}
                    columns={componentInfoColumns}
                    onRowClick={(rowData) => {
                      onComponentInfoRowClick(rowData);
                    }}
                    loading={shouldShowComponentInfoSkeleton}
                    totalPages={componentInfoTotalPages}
                    totalData={componentInfoTotalData}
                    currentPage={componentInfoCurrentPage}
                    setCurrentPage={setComponentInfoCurrentPage}
                    maxRowsPerPage={componentInfoItemsPerPage}
                    onItemsPerPageChange={handleComponentInfoItemsPerPageChangeWithReset}
                    selectedRows={[]}
                    onCheckboxChange={() => {}}
                    disabledRow={[]}
                    hasCheckbox={false}
                    hasRadioButton={false}
                    shouldShowGoToPage={true}
                    showItemsPerPageSelector={true}
                  />
                  {shouldShowComponentInfoOverlay && (
                    <div className="pointer-events-none absolute inset-0 z-10">
                      <LoadingSpinner bgClass="bg-transparent" size={8} data-testid="component-usage-info-loading-overlay" />
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
